import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardTitle,
  cn,
  IrmMountainIcon,
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
  Label,
  ScrollArea,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { useCompany, useUserInfo } from "@query";
import { APP_BASE_PATH } from "@shared";
import {
  AtSign,
  Building2,
  CalendarDays,
  Globe,
  Globe2,
  Landmark,
  type LucideIcon,
  Mail,
  Pencil,
  User,
  UserRound,
} from "lucide-react";
import { useMemo, useState } from "react";
import { EditProfileDialog } from "../components/EditProfileDialog";

function formatDate(value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function getInitials(firstName: string, lastName: string): string {
  const first = firstName?.trim().charAt(0) ?? "";
  const last = lastName?.trim().charAt(0) ?? "";
  return (first + last).toUpperCase() || "U";
}

function ProfileFieldCard({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string | number;
}) {
  return (
    <Item variant="outline">
      <ItemMedia variant="icon">
        <Icon aria-hidden />
      </ItemMedia>
      <ItemContent>
        <ItemDescription>{label}</ItemDescription>
        <ItemTitle className="break-all">{value}</ItemTitle>
      </ItemContent>
    </Item>
  );
}

export function MyProfilePage() {
  const { data: userInfo, isLoading } = useUserInfo(APP_BASE_PATH, {
    refetchOnMount: "always",
  });
  const { t } = useTranslation();
  const { data: company } = useCompany(userInfo?.companyUuid, APP_BASE_PATH);
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  const displayName =
    `${userInfo?.firstName ?? ""} ${userInfo?.lastName ?? ""}`.trim() ||
    userInfo?.loginId ||
    t("hub.sidebar.user");

  const fields: { icon: LucideIcon; label: string; value: string | number }[] =
    useMemo(
      () => [
        {
          icon: Building2,
          label: t("hub.profile.company_id"),
          value: userInfo?.companyId ?? "—",
        },
        {
          icon: Landmark,
          label: t("hub.profile.company_name"),
          value: company?.name?.trim() || "—",
        },
        {
          icon: User,
          label: t("hub.profile.first_name"),
          value: userInfo?.firstName || "—",
        },
        {
          icon: UserRound,
          label: t("hub.profile.last_name"),
          value: userInfo?.lastName || "—",
        },
        {
          icon: AtSign,
          label: t("hub.profile.login_id"),
          value: userInfo?.loginId || "—",
        },
        {
          icon: Mail,
          label: t("auth.login.username_placeholder"),
          value: userInfo?.email || "—",
        },
        {
          icon: Globe2,
          label: t("hub.profile.timezone"),
          value: userInfo?.timezone || "—",
        },
        {
          icon: CalendarDays,
          label: t("hub.profile.created_at"),
          value: formatDate(userInfo?.createdAt),
        },
        {
          icon: Globe,
          label: t("hub.profile.interface_language"),
          value: userInfo?.language || "—",
        },
      ],
      [t, userInfo, company],
    );
  return (
    <ScrollArea className="min-h-0 flex-1">
      <div className="space-y-6">
        <Card
          className="border-0 shadow-md max-w-3xl mx-auto"
          isLoading={isLoading}>
          <div
            className={cn(
              "relative overflow-hidden bg-linear-to-br from-primary/15 via-card to-muted/40",
              "px-6 pt-8 pb-6",
            )}>
            <IrmMountainIcon
              className="pointer-events-none absolute -right-4 -bottom-6 size-40 text-primary-foreground/10 sm:size-48"
              aria-hidden
            />
            <div className="relative flex flex-col items-center gap-5 sm:flex-row sm:items-center">
              <Avatar className="size-24 border-4 border-background shadow-lg sm:size-28">
                <AvatarFallback className="bg-primary text-2xl font-bold text-primary-foreground sm:text-3xl">
                  {getInitials(
                    userInfo?.firstName ?? "",
                    userInfo?.lastName ?? "",
                  )}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2">
                  <CardTitle
                    className={cn(
                      "text-2xl font-bold tracking-tight text-foreground sm:text-3xl",
                      "text-balance leading-tight",
                    )}>
                    {displayName}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => setEditProfileOpen(true)}
                    aria-label={t("hub.profile.edit_profile", {
                      defaultValue: "Edit Profile",
                    })}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
                {userInfo?.email ? (
                  <CardDescription className="mt-1 flex items-center justify-center gap-1.5 sm:justify-start">
                    <Mail
                      className="size-3.5 shrink-0 opacity-70"
                      aria-hidden
                    />
                    <Label className="min-w-0 cursor-default truncate font-normal text-inherit">
                      {userInfo?.email}
                    </Label>
                  </CardDescription>
                ) : null}
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  {userInfo?.userType ? (
                    <Badge variant="secondary" className="font-normal">
                      {userInfo?.userType}
                    </Badge>
                  ) : null}
                  <Badge
                    variant={userInfo?.isActive ? "primary" : "outline"}
                    className="font-normal">
                    {userInfo?.isActive
                      ? t("hub.profile.tenant_active")
                      : t("hub.profile.tenant_inactive")}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          <CardContent className="border-border/50 border-t p-4 sm:p-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {fields.map(({ icon, label, value }) => (
                <ProfileFieldCard
                  key={label}
                  icon={icon}
                  label={label}
                  value={value}
                />
              ))}
            </div>
          </CardContent>
        </Card>
        {userInfo && (
          <EditProfileDialog
            open={editProfileOpen && !!userInfo}
            onOpenChange={setEditProfileOpen}
            user={userInfo}
          />
        )}
      </div>
    </ScrollArea>
  );
}
